import type {
    Absence,
    AssignPaycodeParams,
    EditPaycodeParams,
    UpdatePaycodeParams,
    DeleteShiftParams,
    GetAbsences,
    GetPaycodeAssign,
    GetPaycodeAssignById,
    GetPaycodeDuration,
    GetPaycodes,
    GetShifts,
    AddShiftParams,
    UpdateShiftParams,
    DeletePaycodeParams,
    Paycode,
    PaycodeAssign,
    PaycodeDuration,
    Shift,
    AddShiftResult,
} from '../types/services';
import { instanceSchedule, instanceTimekeeper } from '../../../../services/instances';

const getPaycodeAssign = async ({ params, alert }: GetPaycodeAssign): Promise<{ paycodeAssigns: PaycodeAssign[] }> => {
    const { showAlert, t } = alert;

    let paycodeAssigns: PaycodeAssign[] = [];

    await instanceTimekeeper
        .get('/paycode_assign', { params })
        .then((response) => {
            paycodeAssigns = response.data.data.map((paycodeAssign: PaycodeAssign) => paycodeAssign);
        })
        .catch(() => {
            showAlert('error', t('alerts.error_get_paycodes'));
        });

    return {
        paycodeAssigns,
    };
};

const getPaycodeAssignById = async ({ params, alert }: GetPaycodeAssignById): Promise<{ paycodeAssign: PaycodeAssign | null }> => {
    const { showAlert, t } = alert;

    let paycodeAssign: PaycodeAssign | null = null;

    await instanceTimekeeper
        .get(`/paycode_assign/${params.paycode_assign_id}`)
        .then((response) => {
            paycodeAssign = response.data.data;
        })
        .catch(() => {
            showAlert('error', t('alerts.error_get_paycodes'));
        });

    return {
        paycodeAssign,
    };
};

const getPaycodeDuration = async ({ params, alert }: GetPaycodeDuration): Promise<{ paycodeDuration: PaycodeDuration[] }> => {
    const { showAlert, t } = alert;

    let paycodeDuration: PaycodeDuration[] = [];

    await instanceTimekeeper
        .get('/durationpaycode_r_employee', {
            params: params,
        })
        .then((response) => {
            paycodeDuration = response.data.data.map((paycodeDuration: PaycodeDuration) => paycodeDuration);
        })
        .catch(() => {
            showAlert('error', t('alerts.error_get_paycode_duration'));
        });

    return {
        paycodeDuration,
    };
};

const getShifts = async ({ params, alert }: GetShifts): Promise<{ shifts: Shift[] }> => {
    const { showAlert, t } = alert;

    let shifts: Shift[] = [];

    await instanceSchedule
        .get('/shift/schedule', { params })
        .then((response) => {
            shifts = response.data.data.map((shift: Shift) => shift);
        })
        .catch(() => {
            showAlert('error', t('alerts.error_get_shifts'));
        });

    return {
        shifts,
    };
};

const addShift = async ({ body, alert }: AddShiftParams): Promise<AddShiftResult | null> => {
    const { showAlert, t } = alert;

    try {
        const response = await instanceSchedule.post('/shift/assign/v2/', body);
        return response.data.data as AddShiftResult;
    } catch (error) {
        console.error('[addShift] Error assigning shifts', error);
        showAlert('error', t('add_shift.alerts.add_error.message'));
        return null;
    }
};

const updateShift = async ({ shiftId, body, alert }: UpdateShiftParams): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceSchedule.patch(`/shift/v2/${shiftId}`, body);
        showAlert('success', t('edit_shift.alerts.edit_success.message'));
        return true;
    } catch (error) {
        console.error('[updateShift] Error updating shift', error);
        showAlert('error', t('edit_shift.alerts.edit_error.message'));
        return false;
    }
};

const deleteShift = async ({ shiftId, alert }: DeleteShiftParams): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceSchedule.delete(`/shift/${shiftId}`);
        showAlert('success', t('delete_shift.alerts.delete_success.message'));
        return true;
    } catch (error) {
        console.error('[deleteShift] Error deleting shift', error);
        showAlert('error', t('delete_shift.alerts.delete_error.message'));
        return false;
    }
};

const getPaycodes = async ({ params, alert }: GetPaycodes): Promise<{ paycodes: Paycode[] }> => {
    const { showAlert, t } = alert;

    let paycodes: Paycode[] = [];

    await instanceTimekeeper
        .get('/paycodes', { params })
        .then((response) => {
            paycodes = response.data.data as Paycode[];
        })
        .catch(() => {
            showAlert('error', t('add_paycode.alerts.get_paycodes_error.message'));
        });

    return { paycodes };
};

const assignPaycode = async ({ body, alert }: AssignPaycodeParams): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceTimekeeper.post('/paycode_assign', body);
        return true;
    } catch {
        showAlert('error', t('add_paycode.alerts.save_error.message'));
        return false;
    }
};

const editPaycode = async ({ paycodeAssignId, body, alert }: EditPaycodeParams): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceTimekeeper.patch(`/paycode_assign/${paycodeAssignId}`, body);
        showAlert('success', t('edit_paycode.alerts.save_success.message'));
        return true;
    } catch {
        showAlert('error', t('edit_paycode.alerts.save_error.message'));
        return false;
    }
};

const updatePaycode = async ({ paycodeAssignId, body, alert }: UpdatePaycodeParams): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceTimekeeper.patch(`/paycode_assign/${paycodeAssignId}`, body);
        return true;
    } catch {
        showAlert('error', t('edit_paycode.alerts.save_error.message'));
        return false;
    }
};

const deletePaycode = async ({ paycodeAssignId, alert }: DeletePaycodeParams): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceTimekeeper.delete(`/paycode_assign/${paycodeAssignId}`);
        showAlert('success', t('delete_paycode.alerts.delete_success.message'));
        return true;
    } catch (error) {
        console.error('[deletePaycode] Error deleting paycode assign', error);
        showAlert('error', t('delete_paycode.alerts.delete_error.message'));
        return false;
    }
};

const getAbsences = async ({ params, alert }: GetAbsences): Promise<{ absences: Absence[] }> => {
    const { showAlert, t } = alert;

    let absences: Absence[] = [];

    await instanceSchedule
        .get('/absence-extend-shift', {
            params,
        })
        .then((response) => {
            const person_ids = Object.keys(response.data.data);

            absences = person_ids.flatMap((person_id) => {
                const personAbsences = response.data.data[person_id];
                return personAbsences.map((absence: Absence) => absence);
            });
        })
        .catch(() => {
            showAlert('error', t('alerts.error_get_absences'));
        });

    return {
        absences,
    };
};

export {
    getPaycodeAssign,
    getPaycodeAssignById,
    getPaycodeDuration,
    getShifts,
    addShift,
    updateShift,
    getAbsences,
    getPaycodes,
    assignPaycode,
    editPaycode,
    updatePaycode,
    deletePaycode,
    deleteShift,
};
